import React from 'react';

/**
 * Icon wrapper for consistent SVG styling
 */
const IconWrapper = ({ children, color = '#00c8ff', size = 24 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke={color}
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

// ============ DRAW TOOLS ============

export const SelectIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Arrow cursor */}
    <path d="M4 4l7 16 2-6 6-2L4 4z" fill={color} fillOpacity="0.15" />
    <path d="M4 4l7 16 2-6 6-2L4 4z" />
    <path d="M13 13l6 6" />
  </IconWrapper>
);

export const WallIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Wall/brick pattern */}
    <rect x="2" y="4" width="20" height="16" rx="1" fill={color} fillOpacity="0.15" />
    <rect x="2" y="4" width="20" height="16" rx="1" />
    {/* Brick lines */}
    <line x1="2" y1="10" x2="22" y2="10" />
    <line x1="2" y1="16" x2="22" y2="16" />
    <line x1="8" y1="4" x2="8" y2="10" />
    <line x1="16" y1="4" x2="16" y2="10" />
    <line x1="12" y1="10" x2="12" y2="16" />
    <line x1="5" y1="16" x2="5" y2="20" />
    <line x1="18" y1="16" x2="18" y2="20" />
  </IconWrapper>
);

export const DoorIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Door frame */}
    <rect x="5" y="2" width="14" height="20" rx="1" fill={color} fillOpacity="0.15" />
    <rect x="5" y="2" width="14" height="20" rx="1" />
    {/* Door panel */}
    <rect x="7" y="4" width="10" height="16" />
    {/* Door handle */}
    <circle cx="15" cy="13" r="1.5" fill={color} />
  </IconWrapper>
);

export const WindowIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Window frame */}
    <rect x="3" y="4" width="18" height="16" rx="1" fill={color} fillOpacity="0.15" />
    <rect x="3" y="4" width="18" height="16" rx="1" />
    {/* Cross dividers */}
    <line x1="12" y1="4" x2="12" y2="20" />
    <line x1="3" y1="12" x2="21" y2="12" />
    {/* Glass panes */}
    <rect x="4" y="5" width="7" height="6" fill={color} fillOpacity="0.1" />
    <rect x="13" y="5" width="7" height="6" fill={color} fillOpacity="0.1" />
  </IconWrapper>
);

export const RoomIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Room outline */}
    <rect x="3" y="3" width="18" height="18" fill={color} fillOpacity="0.15" />
    <rect x="3" y="3" width="18" height="18" />
    {/* Corner markers */}
    <path d="M3 7V3h4" />
    <path d="M17 3h4v4" />
    <path d="M21 17v4h-4" />
    <path d="M7 21H3v-4" />
    {/* Dimension lines */}
    <line x1="7" y1="12" x2="17" y2="12" strokeDasharray="2,2" />
    <line x1="12" y1="7" x2="12" y2="17" strokeDasharray="2,2" />
  </IconWrapper>
);

export const PostIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Beam carried by the post */}
    <path d="M3 5h18" />
    {/* Post shaft */}
    <rect x="10" y="5" width="4" height="14" fill={color} fillOpacity="0.2" />
    <rect x="10" y="5" width="4" height="14" />
    {/* Base */}
    <rect x="8" y="19" width="8" height="2" fill={color} fillOpacity="0.3" />
    <rect x="8" y="19" width="8" height="2" />
  </IconWrapper>
);

export const RoofIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Roof shape */}
    <path d="M2 12l10-8 10 8" fill={color} fillOpacity="0.15" />
    <path d="M2 12l10-8 10 8" />
    {/* House body */}
    <path d="M4 12v9h16v-9" fill={color} fillOpacity="0.1" />
    <path d="M4 12v9h16v-9" />
    {/* Chimney */}
    <rect x="15" y="6" width="3" height="5" fill={color} fillOpacity="0.2" />
    <rect x="15" y="6" width="3" height="5" />
  </IconWrapper>
);

// ============ MODIFY TOOLS ============

export const MoveIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Four-way arrows */}
    <path d="M12 2v20M2 12h20" />
    {/* Arrow heads */}
    <path d="M12 2l-3 3M12 2l3 3" />
    <path d="M12 22l-3-3M12 22l3-3" />
    <path d="M2 12l3-3M2 12l3 3" />
    <path d="M22 12l-3-3M22 12l-3 3" />
    {/* Center dot */}
    <circle cx="12" cy="12" r="2" fill={color} fillOpacity="0.3" />
  </IconWrapper>
);

export const RotateIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Circular arrow */}
    <path d="M21 12a9 9 0 11-6.22-8.56" />
    <path d="M21 3v5h-5" />
    {/* Center point */}
    <circle cx="12" cy="12" r="2" fill={color} fillOpacity="0.3" />
  </IconWrapper>
);

export const ExtendIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Original line */}
    <line x1="4" y1="12" x2="12" y2="12" strokeWidth="2" />
    {/* Extended part */}
    <line x1="12" y1="12" x2="20" y2="12" strokeDasharray="3,2" />
    {/* Arrows */}
    <path d="M8 8l-4 4 4 4" />
    <path d="M16 8l4 4-4 4" />
  </IconWrapper>
);

export const TrimIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Scissors */}
    <circle cx="6" cy="6" r="3" fill={color} fillOpacity="0.15" />
    <circle cx="6" cy="18" r="3" fill={color} fillOpacity="0.15" />
    <path d="M6 6l12 12M6 18L18 6" />
    {/* Cutting line */}
    <line x1="14" y1="4" x2="14" y2="20" strokeDasharray="2,2" opacity="0.5" />
  </IconWrapper>
);

export const CornerIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Corner angle */}
    <path d="M4 20V8h16" fill={color} fillOpacity="0.15" />
    <path d="M4 20V8h16" />
    {/* Corner indicator */}
    <rect x="4" y="8" width="6" height="6" fill={color} fillOpacity="0.2" />
    {/* Angle arc */}
    <path d="M4 14a6 6 0 016-6" strokeDasharray="2,1" />
  </IconWrapper>
);

export const ChamferIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* L-shape with chamfer */}
    <path d="M4 20V4h6l10 10v6H4z" fill={color} fillOpacity="0.15" />
    <path d="M4 20V4h6l10 10v6H4z" />
    {/* Chamfer highlight */}
    <line x1="10" y1="4" x2="20" y2="14" strokeWidth="2" />
  </IconWrapper>
);

export const FilletIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* L-shape with fillet */}
    <path d="M4 20V10a6 6 0 016-6h10v16H4z" fill={color} fillOpacity="0.15" />
    <path d="M4 20V10a6 6 0 016-6h10" />
    <path d="M20 4v16H4" />
    {/* Fillet arc highlight */}
    <path d="M4 10a6 6 0 016-6" strokeWidth="2" />
  </IconWrapper>
);

export const BreakIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Left wall segment */}
    <line x1="2" y1="12" x2="9" y2="12" strokeWidth="2" />
    {/* Right wall segment */}
    <line x1="15" y1="12" x2="22" y2="12" strokeWidth="2" />
    {/* Break point indicator - vertical intersecting line */}
    <line x1="12" y1="4" x2="12" y2="20" strokeDasharray="2,2" opacity="0.6" />
    {/* Break points */}
    <circle cx="9" cy="12" r="2" fill={color} />
    <circle cx="15" cy="12" r="2" fill={color} />
  </IconWrapper>
);

// ============ ANNOTATE TOOLS ============

export const DimensionIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Dimension line */}
    <line x1="4" y1="12" x2="20" y2="12" />
    {/* End ticks */}
    <line x1="4" y1="8" x2="4" y2="16" />
    <line x1="20" y1="8" x2="20" y2="16" />
    {/* Arrows */}
    <path d="M4 12l3-2M4 12l3 2" />
    <path d="M20 12l-3-2M20 12l-3 2" />
    {/* Text placeholder */}
    <rect x="9" y="6" width="6" height="4" rx="1" fill={color} fillOpacity="0.2" />
  </IconWrapper>
);

export const TextIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* T shape */}
    <path d="M4 6h16" strokeWidth="2" />
    <path d="M12 6v14" strokeWidth="2" />
    {/* Serifs */}
    <path d="M8 20h8" strokeWidth="2" />
  </IconWrapper>
);

export const LineIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Diagonal line */}
    <line x1="4" y1="20" x2="20" y2="4" strokeWidth="2" />
    {/* End points */}
    <circle cx="4" cy="20" r="2" fill={color} />
    <circle cx="20" cy="4" r="2" fill={color} />
  </IconWrapper>
);

export const PolylineIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Connected line segments */}
    <path d="M3 17l5-10 4 6 5-8 4 12" fill={color} fillOpacity="0.1" />
    <path d="M3 17l5-10 4 6 5-8 4 12" />
    {/* Vertices */}
    <circle cx="3" cy="17" r="1.5" fill={color} />
    <circle cx="8" cy="7" r="1.5" fill={color} />
    <circle cx="12" cy="13" r="1.5" fill={color} />
    <circle cx="17" cy="5" r="1.5" fill={color} />
    <circle cx="21" cy="17" r="1.5" fill={color} />
  </IconWrapper>
);

export const HatchIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Rectangle with hatch pattern */}
    <rect x="3" y="3" width="18" height="18" fill={color} fillOpacity="0.1" />
    <rect x="3" y="3" width="18" height="18" />
    {/* Diagonal hatch lines */}
    <line x1="3" y1="9" x2="9" y2="3" />
    <line x1="3" y1="15" x2="15" y2="3" />
    <line x1="3" y1="21" x2="21" y2="3" />
    <line x1="9" y1="21" x2="21" y2="9" />
    <line x1="15" y1="21" x2="21" y2="15" />
  </IconWrapper>
);

// ============ VIEW TOOLS ============

export const PanIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Hand outline */}
    <path d="M18 11V6a2 2 0 00-4 0v5" />
    <path d="M14 10V4a2 2 0 00-4 0v6" />
    <path d="M10 10V6a2 2 0 00-4 0v8" />
    <path d="M18 11a2 2 0 014 0v3c0 4-3 7-7 7h-2c-3 0-6-2-7-5l-2-4a2 2 0 013-2l1 1" />
  </IconWrapper>
);

export const ZoomFitIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Magnifying glass */}
    <circle cx="10" cy="10" r="7" fill={color} fillOpacity="0.15" />
    <circle cx="10" cy="10" r="7" />
    <line x1="15" y1="15" x2="21" y2="21" strokeWidth="2" />
    {/* Fit arrows inside */}
    <path d="M7 7l6 6M13 7l-6 6" />
  </IconWrapper>
);

export const ZoomInIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Magnifying glass */}
    <circle cx="10" cy="10" r="7" fill={color} fillOpacity="0.15" />
    <circle cx="10" cy="10" r="7" />
    <line x1="15" y1="15" x2="21" y2="21" strokeWidth="2" />
    {/* Plus sign */}
    <line x1="10" y1="7" x2="10" y2="13" />
    <line x1="7" y1="10" x2="13" y2="10" />
  </IconWrapper>
);

export const ZoomOutIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Magnifying glass */}
    <circle cx="10" cy="10" r="7" fill={color} fillOpacity="0.15" />
    <circle cx="10" cy="10" r="7" />
    <line x1="15" y1="15" x2="21" y2="21" strokeWidth="2" />
    {/* Minus sign */}
    <line x1="7" y1="10" x2="13" y2="10" />
  </IconWrapper>
);

export const ResetViewIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Circular refresh arrow */}
    <path d="M3 12a9 9 0 109-9" />
    <path d="M12 3v5h5" />
    {/* Center square */}
    <rect x="9" y="9" width="6" height="6" fill={color} fillOpacity="0.2" />
  </IconWrapper>
);

export const View3DIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* 3D cube */}
    <path d="M12 2l9 5v10l-9 5-9-5V7l9-5z" fill={color} fillOpacity="0.15" />
    <path d="M12 2l9 5v10l-9 5-9-5V7l9-5z" />
    {/* Internal cube lines */}
    <path d="M12 12l9-5M12 12v10M12 12L3 7" />
  </IconWrapper>
);

export const ElevationsIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Building elevation */}
    <rect x="3" y="8" width="18" height="14" fill={color} fillOpacity="0.15" />
    <rect x="3" y="8" width="18" height="14" />
    {/* Roof */}
    <path d="M1 8l11-6 11 6" />
    {/* Windows */}
    <rect x="6" y="11" width="4" height="4" />
    <rect x="14" y="11" width="4" height="4" />
    {/* Door */}
    <rect x="10" y="16" width="4" height="6" />
  </IconWrapper>
);

// ============ MARKUP TOOLS ============

export const AnnotationSelectIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Arrow cursor */}
    <path d="M5 3l12 9-5 1-2 5-5-15z" fill={color} fillOpacity="0.2" />
    <path d="M5 3l12 9-5 1-2 5-5-15z" />
  </IconWrapper>
);

export const ArrowIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Arrow line */}
    <line x1="5" y1="19" x2="19" y2="5" strokeWidth="2" />
    {/* Arrow head */}
    <path d="M19 5l-6 1 5 5" fill={color} />
    <path d="M19 5l-6 1 5 5" />
  </IconWrapper>
);

export const FreehandIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Pencil */}
    <path d="M17 3l4 4-14 14H3v-4L17 3z" fill={color} fillOpacity="0.15" />
    <path d="M17 3l4 4-14 14H3v-4L17 3z" />
    <line x1="15" y1="5" x2="19" y2="9" />
    {/* Wavy line */}
    <path d="M3 21c2-2 4-2 6 0s4 2 6 0" strokeLinecap="round" />
  </IconWrapper>
);

export const CircleIcon = ({ color }) => (
  <IconWrapper color={color}>
    <circle cx="12" cy="12" r="9" fill={color} fillOpacity="0.15" />
    <circle cx="12" cy="12" r="9" />
  </IconWrapper>
);

export const RectangleIcon = ({ color }) => (
  <IconWrapper color={color}>
    <rect x="3" y="5" width="18" height="14" rx="1" fill={color} fillOpacity="0.15" />
    <rect x="3" y="5" width="18" height="14" rx="1" />
  </IconWrapper>
);

export const CloudIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Cloud shape */}
    <path d="M18 10a4 4 0 00-7.5-2A5 5 0 106 18h12a4 4 0 000-8z" fill={color} fillOpacity="0.15" />
    <path d="M18 10a4 4 0 00-7.5-2A5 5 0 106 18h12a4 4 0 000-8z" />
  </IconWrapper>
);

export const StampIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Stamp body */}
    <rect x="6" y="3" width="12" height="10" rx="1" fill={color} fillOpacity="0.15" />
    <rect x="6" y="3" width="12" height="10" rx="1" />
    {/* Handle */}
    <rect x="10" y="6" width="4" height="4" fill={color} />
    {/* Base */}
    <rect x="4" y="17" width="16" height="4" rx="1" fill={color} fillOpacity="0.2" />
    <rect x="4" y="17" width="16" height="4" rx="1" />
    {/* Connection */}
    <line x1="12" y1="13" x2="12" y2="17" />
  </IconWrapper>
);

export const CalloutIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Circle with number */}
    <circle cx="12" cy="12" r="9" fill={color} fillOpacity="0.2" />
    <circle cx="12" cy="12" r="9" />
    {/* Number 1 */}
    <text x="12" y="16" textAnchor="middle" fill={color} fontSize="10" fontWeight="bold" stroke="none">1</text>
  </IconWrapper>
);

export const NotesIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Notepad */}
    <rect x="4" y="3" width="16" height="18" rx="1" fill={color} fillOpacity="0.15" />
    <rect x="4" y="3" width="16" height="18" rx="1" />
    {/* Lines */}
    <line x1="8" y1="8" x2="16" y2="8" />
    <line x1="8" y1="12" x2="16" y2="12" />
    <line x1="8" y1="16" x2="12" y2="16" />
    {/* Spiral binding */}
    <circle cx="4" cy="6" r="1" fill={color} />
    <circle cx="4" cy="10" r="1" fill={color} />
    <circle cx="4" cy="14" r="1" fill={color} />
    <circle cx="4" cy="18" r="1" fill={color} />
  </IconWrapper>
);

export const SettingsIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Gear */}
    <circle cx="12" cy="12" r="3" fill={color} fillOpacity="0.2" />
    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    <circle cx="12" cy="12" r="4" />
  </IconWrapper>
);

// ============ ACTION BUTTONS ============

export const UndoIcon = ({ color }) => (
  <IconWrapper color={color}>
    <path d="M3 10h11a5 5 0 015 5v2" />
    <path d="M3 10l5-5M3 10l5 5" />
  </IconWrapper>
);

export const RedoIcon = ({ color }) => (
  <IconWrapper color={color}>
    <path d="M21 10H10a5 5 0 00-5 5v2" />
    <path d="M21 10l-5-5M21 10l-5 5" />
  </IconWrapper>
);

export const DeleteIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Trash can */}
    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" fill={color} fillOpacity="0.15" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </IconWrapper>
);

export const SelectAllIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Selection box with all corners */}
    <rect x="3" y="3" width="18" height="18" rx="2" fill={color} fillOpacity="0.15" />
    <rect x="3" y="3" width="18" height="18" rx="2" />
    {/* Checkmark */}
    <path d="M8 12l3 3 5-6" strokeWidth="2" />
  </IconWrapper>
);

export const DeselectIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Selection box */}
    <rect x="3" y="3" width="18" height="18" rx="2" strokeDasharray="3,2" />
    {/* X mark */}
    <path d="M9 9l6 6M15 9l-6 6" strokeWidth="2" />
  </IconWrapper>
);

export const DetectRoomsIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Floor plan outline with detected rooms */}
    <rect x="3" y="3" width="18" height="18" rx="1" />
    {/* Internal walls creating rooms */}
    <line x1="12" y1="3" x2="12" y2="14" />
    <line x1="3" y1="12" x2="12" y2="12" />
    {/* Room fills */}
    <rect x="4" y="4" width="7" height="7" fill={color} fillOpacity="0.2" />
    <rect x="4" y="13" width="7" height="7" fill={color} fillOpacity="0.15" />
    <rect x="13" y="4" width="7" height="16" fill={color} fillOpacity="0.1" />
    {/* Magnifying glass to indicate detection */}
    <circle cx="17" cy="17" r="3" fill="none" strokeWidth="1.5" />
    <line x1="19" y1="19" x2="21" y2="21" strokeWidth="2" />
  </IconWrapper>
);

export const FurnitureIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Couch */}
    <path d="M4 12V8a2 2 0 012-2h12a2 2 0 012 2v4" fill={color} fillOpacity="0.15" />
    <path d="M4 12V8a2 2 0 012-2h12a2 2 0 012 2v4" />
    <rect x="2" y="12" width="20" height="6" rx="1" fill={color} fillOpacity="0.2" />
    <rect x="2" y="12" width="20" height="6" rx="1" />
    {/* Legs */}
    <line x1="5" y1="18" x2="5" y2="21" />
    <line x1="19" y1="18" x2="19" y2="21" />
    {/* Cushion divider */}
    <line x1="12" y1="12" x2="12" y2="18" />
  </IconWrapper>
);

export const LayersIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Stacked layers */}
    <path d="M12 2l10 5-10 5-10-5 10-5z" fill={color} fillOpacity="0.15" />
    <path d="M12 2l10 5-10 5-10-5 10-5z" />
    <path d="M2 12l10 5 10-5" />
    <path d="M2 17l10 5 10-5" />
  </IconWrapper>
);

export const SheetsIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Stacked documents */}
    <rect x="6" y="4" width="14" height="18" rx="1" fill={color} fillOpacity="0.15" />
    <rect x="6" y="4" width="14" height="18" rx="1" />
    <rect x="4" y="2" width="14" height="18" rx="1" fill={color} fillOpacity="0.1" />
    <rect x="4" y="2" width="14" height="18" rx="1" />
    {/* Lines */}
    <line x1="8" y1="8" x2="16" y2="8" />
    <line x1="8" y1="12" x2="16" y2="12" />
    <line x1="8" y1="16" x2="12" y2="16" />
  </IconWrapper>
);

export const PanelIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Corral panel: two uprights, five rails */}
    <path d="M3 5v15M21 5v15" />
    <path d="M3 6h18M3 9.5h18M3 13h18M3 16.5h18M3 19.5h18" strokeWidth="1.2" />
  </IconWrapper>
);

export const TerrainIcon = ({ color }) => (
  <IconWrapper color={color}>
    <path d="M2 18l5-7 4 4 3-4 8 7H2z" fill={color} fillOpacity="0.15" />
    <path d="M2 21h20" />
    <path d="M17 3v6M15 7l2 2 2-2" />
  </IconWrapper>
);

export const PDFIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Document with PDF text */}
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill={color} fillOpacity="0.15" />
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
    <path d="M14 2v6h6" />
    {/* PDF text */}
    <text x="12" y="16" textAnchor="middle" fill={color} fontSize="6" fontWeight="bold" stroke="none">PDF</text>
  </IconWrapper>
);

export const CalculatorIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Calculator body */}
    <rect x="4" y="2" width="16" height="20" rx="2" fill={color} fillOpacity="0.15" />
    <rect x="4" y="2" width="16" height="20" rx="2" />
    {/* Display */}
    <rect x="6" y="4" width="12" height="4" rx="1" fill={color} fillOpacity="0.2" />
    {/* Buttons grid */}
    <rect x="6" y="10" width="3" height="2" rx="0.5" />
    <rect x="10.5" y="10" width="3" height="2" rx="0.5" />
    <rect x="15" y="10" width="3" height="2" rx="0.5" />
    <rect x="6" y="14" width="3" height="2" rx="0.5" />
    <rect x="10.5" y="14" width="3" height="2" rx="0.5" />
    <rect x="15" y="14" width="3" height="2" rx="0.5" />
    <rect x="6" y="18" width="3" height="2" rx="0.5" />
    <rect x="10.5" y="18" width="3" height="2" rx="0.5" />
    <rect x="15" y="18" width="3" height="2" rx="0.5" />
  </IconWrapper>
);

export const ShareIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Share network nodes */}
    <circle cx="18" cy="5" r="3" fill={color} fillOpacity="0.2" />
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" fill={color} fillOpacity="0.2" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" fill={color} fillOpacity="0.2" />
    <circle cx="18" cy="19" r="3" />
    {/* Connecting lines */}
    <line x1="8.59" y1="10.59" x2="15.42" y2="6.41" />
    <line x1="8.59" y1="13.41" x2="15.42" y2="17.59" />
  </IconWrapper>
);

export const ChairIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Chair seat */}
    <rect x="5" y="10" width="14" height="3" rx="1" fill={color} fillOpacity="0.2" />
    <rect x="5" y="10" width="14" height="3" rx="1" />
    {/* Back rest */}
    <rect x="5" y="3" width="14" height="2" rx="1" fill={color} fillOpacity="0.15" />
    <rect x="5" y="3" width="14" height="2" rx="1" />
    {/* Back supports */}
    <line x1="7" y1="5" x2="7" y2="10" />
    <line x1="17" y1="5" x2="17" y2="10" />
    {/* Legs */}
    <line x1="6" y1="13" x2="5" y2="21" />
    <line x1="18" y1="13" x2="19" y2="21" />
    <line x1="10" y1="13" x2="10" y2="21" />
    <line x1="14" y1="13" x2="14" y2="21" />
  </IconWrapper>
);

export const FireSafetyIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Tank body */}
    <rect x="7" y="9" width="10" height="12" rx="2" fill={color} fillOpacity="0.2" />
    <rect x="7" y="9" width="10" height="12" rx="2" />
    {/* Top handle */}
    <rect x="9" y="5" width="6" height="3" rx="0.5" fill={color} fillOpacity="0.15" />
    <rect x="9" y="5" width="6" height="3" rx="0.5" />
    {/* Neck */}
    <line x1="10" y1="8" x2="10" y2="9" />
    <line x1="14" y1="8" x2="14" y2="9" />
    {/* Nozzle */}
    <line x1="15" y1="6.5" x2="18.5" y2="6.5" />
    {/* Label band */}
    <line x1="7" y1="14" x2="17" y2="14" />
  </IconWrapper>
);

export const EstimateIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Clipboard */}
    <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" fill={color} fillOpacity="0.15" />
    <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" />
    {/* Clipboard clip */}
    <rect x="8" y="2" width="8" height="4" rx="1" fill={color} fillOpacity="0.2" />
    <rect x="8" y="2" width="8" height="4" rx="1" />
    {/* Dollar sign */}
    <path d="M12 9v8M9 12h6M9 15h6" />
  </IconWrapper>
);

export const InvoiceIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Receipt/document */}
    <path d="M6 2h12a2 2 0 012 2v16l-3-2-3 2-3-2-3 2V4a2 2 0 012-2z" fill={color} fillOpacity="0.15" />
    <path d="M6 2h12a2 2 0 012 2v16l-3-2-3 2-3-2-3 2V4a2 2 0 012-2z" />
    {/* Lines */}
    <line x1="8" y1="7" x2="16" y2="7" />
    <line x1="8" y1="11" x2="16" y2="11" />
    <line x1="8" y1="15" x2="12" y2="15" />
  </IconWrapper>
);

export const ClientsIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Two people */}
    <circle cx="9" cy="7" r="3" fill={color} fillOpacity="0.2" />
    <circle cx="9" cy="7" r="3" />
    <path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" fill={color} fillOpacity="0.15" />
    <path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" />
    {/* Second person (behind) */}
    <circle cx="16" cy="7" r="3" fill={color} fillOpacity="0.1" />
    <circle cx="16" cy="7" r="3" />
    <path d="M21 21v-2a4 4 0 00-3-3.87" />
  </IconWrapper>
);

export const CompanyIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Building */}
    <rect x="4" y="6" width="16" height="16" rx="1" fill={color} fillOpacity="0.15" />
    <rect x="4" y="6" width="16" height="16" rx="1" />
    {/* Roof/top */}
    <path d="M2 6l10-4 10 4" />
    {/* Windows */}
    <rect x="7" y="10" width="3" height="3" />
    <rect x="14" y="10" width="3" height="3" />
    <rect x="7" y="16" width="3" height="3" />
    <rect x="14" y="16" width="3" height="3" />
    {/* Door */}
    <rect x="10" y="17" width="4" height="5" fill={color} fillOpacity="0.2" />
  </IconWrapper>
);

export const FileIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* File shape */}
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill={color} fillOpacity="0.15" />
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
    <path d="M14 2v6h6" />
  </IconWrapper>
);

export const NewFileIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* File shape with plus */}
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill={color} fillOpacity="0.15" />
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
    <path d="M14 2v6h6" />
    {/* Plus sign */}
    <line x1="12" y1="11" x2="12" y2="17" />
    <line x1="9" y1="14" x2="15" y2="14" />
  </IconWrapper>
);

export const FolderOpenIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Open folder */}
    <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2v11z" fill={color} fillOpacity="0.15" />
    <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2v11z" />
    {/* Folder lid open effect */}
    <path d="M2 10h20" />
  </IconWrapper>
);

export const ClockIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Clock face */}
    <circle cx="12" cy="12" r="10" fill={color} fillOpacity="0.15" />
    <circle cx="12" cy="12" r="10" />
    {/* Clock hands */}
    <path d="M12 6v6l4 2" />
  </IconWrapper>
);

export const SaveIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Floppy disk */}
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" fill={color} fillOpacity="0.15" />
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
    {/* Label area */}
    <rect x="7" y="13" width="10" height="8" fill={color} fillOpacity="0.2" />
    <path d="M17 21v-8H7v8" />
    {/* Top notch */}
    <path d="M7 3v5h8" />
  </IconWrapper>
);

export const ExportIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Arrow pointing out */}
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
    <path d="M17 8l-5-5-5 5" />
    <path d="M12 3v12" />
  </IconWrapper>
);

export const PrintIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Printer body */}
    <rect x="5" y="8" width="14" height="8" rx="1" fill={color} fillOpacity="0.15" />
    <rect x="5" y="8" width="14" height="8" rx="1" />
    {/* Paper input */}
    <path d="M6 8V4a1 1 0 011-1h10a1 1 0 011 1v4" fill={color} fillOpacity="0.1" />
    <path d="M6 8V4a1 1 0 011-1h10a1 1 0 011 1v4" />
    {/* Paper output */}
    <rect x="6" y="16" width="12" height="5" rx="1" fill={color} fillOpacity="0.2" />
    <rect x="6" y="16" width="12" height="5" rx="1" />
    {/* Print lines */}
    <line x1="8" y1="18" x2="16" y2="18" />
    <line x1="8" y1="20" x2="14" y2="20" />
  </IconWrapper>
);

export const ClipboardIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Clipboard body */}
    <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" fill={color} fillOpacity="0.15" />
    <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" />
    {/* Clipboard clip */}
    <rect x="8" y="2" width="8" height="4" rx="1" fill={color} fillOpacity="0.2" />
    <rect x="8" y="2" width="8" height="4" rx="1" />
  </IconWrapper>
);

export const CopyIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Back sheet */}
    <rect x="9" y="9" width="12" height="12" rx="2" fill={color} fillOpacity="0.15" />
    <rect x="9" y="9" width="12" height="12" rx="2" />
    {/* Front sheet */}
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
  </IconWrapper>
);

export const PasteIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Clipboard body */}
    <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" fill={color} fillOpacity="0.15" />
    <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" />
    {/* Clip */}
    <rect x="8" y="2" width="8" height="4" rx="1" fill={color} fillOpacity="0.2" />
    <rect x="8" y="2" width="8" height="4" rx="1" />
    {/* Arrow down into the clipboard, to separate it from Copy */}
    <path d="M12 11v6M9.5 14.5L12 17l2.5-2.5" />
  </IconWrapper>
);

export const GearIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Gear */}
    <circle cx="12" cy="12" r="3" fill={color} fillOpacity="0.2" />
    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    <circle cx="12" cy="12" r="4" />
  </IconWrapper>
);

export const MenuIcon = ({ color }) => (
  <IconWrapper color={color}>
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </IconWrapper>
);

export const TemplatesIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Grid layout representing templates */}
    <rect x="3" y="3" width="8" height="8" rx="1" fill={color} fillOpacity="0.15" />
    <rect x="3" y="3" width="8" height="8" rx="1" />
    <rect x="13" y="3" width="8" height="8" rx="1" fill={color} fillOpacity="0.15" />
    <rect x="13" y="3" width="8" height="8" rx="1" />
    <rect x="3" y="13" width="8" height="8" rx="1" fill={color} fillOpacity="0.15" />
    <rect x="3" y="13" width="8" height="8" rx="1" />
    <rect x="13" y="13" width="8" height="8" rx="1" fill={color} fillOpacity="0.15" />
    <rect x="13" y="13" width="8" height="8" rx="1" />
  </IconWrapper>
);

export const HelpIcon = ({ color }) => (
  <IconWrapper color={color}>
    {/* Question mark circle */}
    <circle cx="12" cy="12" r="10" fill={color} fillOpacity="0.15" />
    <circle cx="12" cy="12" r="10" />
    {/* Question mark */}
    <path d="M9 9c0-1.5 1.5-3 3-3s3 1.5 3 3c0 1.5-1.5 2-2 3" strokeLinecap="round" />
    <circle cx="12" cy="17" r="0.5" fill={color} />
  </IconWrapper>
);

// Export all icons as a map for easy lookup
export const TOOLBAR_ICONS = {
  // Draw
  select: SelectIcon,
  wall: WallIcon,
  door: DoorIcon,
  window: WindowIcon,
  room: RoomIcon,
  roof: RoofIcon,
  post: PostIcon,
  furniture: FurnitureIcon,

  // Modify
  move: MoveIcon,
  rotate: RotateIcon,
  extend: ExtendIcon,
  trim: TrimIcon,
  break: BreakIcon,
  corner: CornerIcon,
  chamfer: ChamferIcon,
  fillet: FilletIcon,

  // Annotate
  dimension: DimensionIcon,
  text: TextIcon,
  line: LineIcon,
  polyline: PolylineIcon,
  hatch: HatchIcon,

  // View
  pan: PanIcon,
  'zoom-fit': ZoomFitIcon,
  zoomIn: ZoomInIcon,
  zoomOut: ZoomOutIcon,
  resetView: ResetViewIcon,
  '3d': View3DIcon,
  view3d: View3DIcon, // alias for easier access
  elevations: ElevationsIcon,

  // Markup
  'annotation-select': AnnotationSelectIcon,
  'annotation-arrow': ArrowIcon,
  'annotation-freehand': FreehandIcon,
  'annotation-circle': CircleIcon,
  'annotation-rectangle': RectangleIcon,
  'annotation-cloud': CloudIcon,
  'annotation-stamp': StampIcon,
  'annotation-callout': CalloutIcon,
  'callout-notes': NotesIcon,
  'markup-settings': SettingsIcon,

  // Actions
  undo: UndoIcon,
  redo: RedoIcon,
  delete: DeleteIcon,
  selectAll: SelectAllIcon,
  deselect: DeselectIcon,
  detectRooms: DetectRoomsIcon,
  layers: LayersIcon,
  menu: MenuIcon,
  sheets: SheetsIcon,
  pdf: PDFIcon,
  terrain: TerrainIcon,
  panel: PanelIcon,
  calculator: CalculatorIcon,
  share: ShareIcon,
  chair: ChairIcon,
  fireSafety: FireSafetyIcon,
  estimate: EstimateIcon,
  invoice: InvoiceIcon,
  clients: ClientsIcon,
  company: CompanyIcon,
  file: FileIcon,
  gear: GearIcon,
  newFile: NewFileIcon,
  folderOpen: FolderOpenIcon,
  clock: ClockIcon,
  save: SaveIcon,
  export: ExportIcon,
  print: PrintIcon,
  clipboard: ClipboardIcon,
  copy: CopyIcon,
  paste: PasteIcon,
  templates: TemplatesIcon,
  help: HelpIcon,
};

export default TOOLBAR_ICONS;
